import { Alert, Platform } from "react-native";

export type ConfirmActionOptions = {
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel: string;
};

type AlertButton = {
  text?: string;
  style?: "default" | "cancel" | "destructive";
  onPress?: () => void;
};

export type ConfirmActionDeps = {
  platform?: string;
  confirm?: (message: string) => boolean;
  alert?: (
    title: string,
    message?: string,
    buttons?: AlertButton[],
    options?: { cancelable?: boolean; onDismiss?: () => void },
  ) => void;
};

/**
 * Native: Alert mit Buttons. Web: window.confirm, weil Alert.alert dort keine Buttons zeigt.
 */
export function confirmAction(
  options: ConfirmActionOptions,
  deps: ConfirmActionDeps = {},
): Promise<boolean> {
  const platform = deps.platform ?? Platform.OS;
  const text = `${options.title}\n\n${options.message}`;
  if (platform === "web") {
    const confirmFn =
      deps.confirm ??
      (typeof globalThis.confirm === "function"
        ? globalThis.confirm.bind(globalThis)
        : () => false);
    return Promise.resolve(confirmFn(text));
  }

  return new Promise((resolve) => {
    let settled = false;
    const finish = (value: boolean) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    const alertFn = deps.alert ?? Alert.alert.bind(Alert);
    const buttons: AlertButton[] = [
      {
        text: options.cancelLabel,
        style: "cancel",
        onPress: () => finish(false),
      },
      {
        text: options.confirmLabel,
        style: "destructive",
        onPress: () => finish(true),
      },
    ];
    if (platform === "android") {
      alertFn(options.title, options.message, buttons, {
        cancelable: true,
        onDismiss: () => finish(false),
      });
      return;
    }
    alertFn(options.title, options.message, buttons);
  });
}
